import { DragListener, FullKeys, FullKeysRecord, RnaComplexProps } from "../../../App";
import { Tab } from "../../../app_data/Tab";
import { BasePairsEditor } from "../../../components/app_specific/editors/BasePairsEditor";
import { NucleotideRegionsAnnotateMenu } from "../../../components/app_specific/menus/annotate_menus/NucleotideRegionsAnnotateMenu";
import { compareBasePairKeys } from "../../../components/app_specific/RnaComplex";
import { BasePairKeysToRerender, NucleotideKeysToRerender } from "../../../context/Context";
import { Vector2D } from "../../../data_structures/Vector2D";
import { canDragCustomSelection, customDomainsToKeys, isNucleotideInKeys } from "../../../utils/Visibility";
import { AbstractInteractionConstraint } from "../AbstractInteractionConstraint";
import { linearDrag } from "../CommonDragListeners";
import { getSortedBasePairs, Helix, InteractionConstraint, iterateOverFreeNucleotidesAndHelicesPerScene } from "../InteractionConstraints";
import { AllInOneEditor } from "./AllInOneEditor";

export class CustomRangeInteractionConstraint extends AbstractInteractionConstraint {
  private readonly dragListener? : DragListener;
  private readonly editMenuProps : AllInOneEditor.SimplifiedProps;
  private readonly initialBasePairs : BasePairsEditor.InitialBasePairs;
  private readonly helices : Array<Helix>;
  private readonly selectedKeys : FullKeysRecord;

  constructor(
    rnaComplexProps : RnaComplexProps,
    setNucleotideKeysToRerender : (nucleotideKeysToRerender : NucleotideKeysToRerender) => void,
    setBasePairKeysToRerender : (basePairKeysToRerender : BasePairKeysToRerender) => void,
    setDebugVisualElements : (debugVisualElements : Array<JSX.Element>) => void,
    tab : Tab,
    indicesOfFrozenNucleotides : FullKeysRecord,
    { customDomains, treatNoncanonicalBasePairsAsUnpairedFlag } : InteractionConstraint.Options,
    fullKeys0 : FullKeys,
    fullKeys1? : FullKeys
  ) {
    super(
      rnaComplexProps,
      setNucleotideKeysToRerender,
      setBasePairKeysToRerender,
      setDebugVisualElements,
      indicesOfFrozenNucleotides,
      fullKeys0,
      fullKeys1
    );
    this.selectedKeys = customDomainsToKeys(rnaComplexProps, customDomains);
    const positions = new Array<Vector2D>();
    const nucleotideKeysToRerender : NucleotideKeysToRerender = {};
    const basePairKeysToRerender : BasePairKeysToRerender = {};
    for (const [rnaComplexIndexAsString, keysPerRnaComplex] of Object.entries(this.selectedKeys)) {
      const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
      nucleotideKeysToRerender[rnaComplexIndex] = {};
      basePairKeysToRerender[rnaComplexIndex] = [];
      const singularRnaComplexProps = rnaComplexProps[rnaComplexIndex];
      for (const [rnaMoleculeName, nucleotideIndices] of Object.entries(keysPerRnaComplex)) {
        nucleotideKeysToRerender[rnaComplexIndex][rnaMoleculeName] = Array.from(nucleotideIndices).sort((a, b) => a - b);
        for (const nucleotideIndex of nucleotideIndices) {
          this.addFullIndices({ rnaComplexIndex, rnaMoleculeName, nucleotideIndex });
          if (!(indicesOfFrozenNucleotides[rnaComplexIndex]?.[rnaMoleculeName]?.has(nucleotideIndex) ?? false)) {
            positions.push(singularRnaComplexProps.rnaMoleculeProps[rnaMoleculeName].nucleotideProps[nucleotideIndex]);
          }
          if (singularRnaComplexProps.basePairs[rnaMoleculeName]?.[nucleotideIndex] !== undefined) {
            basePairKeysToRerender[rnaComplexIndex].push({ rnaMoleculeName, nucleotideIndex });
          }
        }
      }
      basePairKeysToRerender[rnaComplexIndex].sort(compareBasePairKeys);
    }
    if (Object.keys(this.indicesOfAffectedNucleotides).length === 0) {
      const errorMessage = "Configure at least one valid custom domain first.";
      throw Object.assign(new Error(errorMessage), { errorMessage });
    }
    const clickedNucleotide = rnaComplexProps[fullKeys0.rnaComplexIndex]
      .rnaMoleculeProps[fullKeys0.rnaMoleculeName].nucleotideProps[fullKeys0.nucleotideIndex];
    const boundingPositions = positions.length > 0 ? positions : [clickedNucleotide];
    const minimumX = Math.min(...boundingPositions.map(({ x }) => x));
    const maximumX = Math.max(...boundingPositions.map(({ x }) => x));
    const minimumY = Math.min(...boundingPositions.map(({ y }) => y));
    const maximumY = Math.max(...boundingPositions.map(({ y }) => y));
    const center = {
      x : (minimumX + maximumX) * 0.5,
      y : (minimumY + maximumY) * 0.5
    };
    const halfSpan = Math.max(maximumX - minimumX, maximumY - minimumY, 2) * 0.5;
    const boundingVector0 = { x : center.x - halfSpan, y : center.y };
    const boundingVector1 = { x : center.x + halfSpan, y : center.y };
    function rerender() {
      setNucleotideKeysToRerender(structuredClone(nucleotideKeysToRerender));
      setBasePairKeysToRerender(structuredClone(basePairKeysToRerender));
    }
    if (canDragCustomSelection(this.selectedKeys, indicesOfFrozenNucleotides, fullKeys0)) {
      this.dragListener = linearDrag(clickedNucleotide, positions, rerender);
    }
    this.editMenuProps = {
      boundingVector0,
      boundingVector1,
      positions,
      onUpdatePositions : rerender
    };
    this.initialBasePairs = [];
    for (const [rnaComplexIndexAsString, singularRnaComplexProps] of Object.entries(rnaComplexProps)) {
      const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
      for (const { keys0, keys1, mappedBasePair } of getSortedBasePairs(singularRnaComplexProps, treatNoncanonicalBasePairsAsUnpairedFlag)) {
        if (
          isNucleotideInKeys(this.selectedKeys, rnaComplexIndex, keys0.rnaMoleculeName, keys0.nucleotideIndex) &&
          isNucleotideInKeys(this.selectedKeys, rnaComplexIndex, keys1.rnaMoleculeName, keys1.nucleotideIndex)
        ) {
          this.initialBasePairs.push({
            rnaComplexIndex,
            rnaMoleculeName0 : keys0.rnaMoleculeName,
            rnaMoleculeName1 : keys1.rnaMoleculeName,
            nucleotideIndex0 : keys0.nucleotideIndex + singularRnaComplexProps.rnaMoleculeProps[keys0.rnaMoleculeName].firstNucleotideIndex,
            nucleotideIndex1 : keys1.nucleotideIndex + singularRnaComplexProps.rnaMoleculeProps[keys1.rnaMoleculeName].firstNucleotideIndex,
            length : 1,
            type : mappedBasePair.basePairType
          });
        }
      }
    }
    const helices = new Array<Helix>();
    for (const { rnaComplexIndex, helixDataPerRnaMolecules } of iterateOverFreeNucleotidesAndHelicesPerScene(
      rnaComplexProps,
      treatNoncanonicalBasePairsAsUnpairedFlag
    )) {
      for (const { rnaMoleculeName0, helixData } of helixDataPerRnaMolecules) {
        for (const { rnaMoleculeName1, start, stop } of helixData) {
          const length = Math.abs(stop[0] - start[0]) + 1;
          const increment0 = Math.sign(stop[0] - start[0]);
          const increment1 = Math.sign(stop[1] - start[1]);
          let selected = true;
          for (let i = 0; i < length; i++) {
            if (
              !isNucleotideInKeys(this.selectedKeys, rnaComplexIndex, rnaMoleculeName0, start[0] + increment0 * i) ||
              !isNucleotideInKeys(this.selectedKeys, rnaComplexIndex, rnaMoleculeName1, start[1] + increment1 * i)
            ) {
              selected = false;
              break;
            }
          }
          if (selected) {
            helices.push({ rnaComplexIndex, rnaMoleculeName0, rnaMoleculeName1, start, stop });
          }
        }
      }
    }
    this.helices = helices;
  }

  public override drag() {
    return this.dragListener;
  }

  public override createRightClickMenu(tab : InteractionConstraint.SupportedTab) {
    let menu : JSX.Element;
    switch (tab) {
      case Tab.EDIT :
        menu = <AllInOneEditor.Simplified {...this.editMenuProps}/>;
        break;
      case Tab.FORMAT :
        menu = <BasePairsEditor.Component
          rnaComplexProps={this.rnaComplexProps}
          initialBasePairs={this.initialBasePairs}
          approveBasePairs={(basePairs) => {
            for (const basePair of basePairs) {
              const singularRnaComplexProps = this.rnaComplexProps[basePair.rnaComplexIndex];
              const molecule0 = singularRnaComplexProps?.rnaMoleculeProps[basePair.rnaMoleculeName0];
              const molecule1 = singularRnaComplexProps?.rnaMoleculeProps[basePair.rnaMoleculeName1];
              if (molecule0 === undefined || molecule1 === undefined) {
                throw "Custom base pairs must stay inside the configured domains.";
              }
              for (let i = 0; i < basePair.length; i++) {
                if (
                  !isNucleotideInKeys(this.selectedKeys, basePair.rnaComplexIndex, basePair.rnaMoleculeName0, basePair.nucleotideIndex0 - molecule0.firstNucleotideIndex + i) ||
                  !isNucleotideInKeys(this.selectedKeys, basePair.rnaComplexIndex, basePair.rnaMoleculeName1, basePair.nucleotideIndex1 - molecule1.firstNucleotideIndex - i)
                ) {
                  throw "Custom base pairs must stay inside the configured domains.";
                }
              }
            }
          }}
        />;
        break;
      case Tab.ANNOTATE : {
        const regions : NucleotideRegionsAnnotateMenu.Regions = {};
        for (const [rnaComplexIndexAsString, keysPerRnaComplex] of Object.entries(this.selectedKeys)) {
          const rnaComplexIndex = Number.parseInt(rnaComplexIndexAsString);
          regions[rnaComplexIndex] = {};
          for (const [rnaMoleculeName, nucleotideIndices] of Object.entries(keysPerRnaComplex)) {
            regions[rnaComplexIndex][rnaMoleculeName] = [];
            NucleotideRegionsAnnotateMenu.populateRegions(
              this.indicesOfFrozenNucleotides[rnaComplexIndex]?.[rnaMoleculeName] ?? new Set<number>(),
              Array.from(nucleotideIndices),
              regions[rnaComplexIndex][rnaMoleculeName]
            );
          }
        }
        menu = <NucleotideRegionsAnnotateMenu.Component
          regions={regions}
          rnaComplexProps={this.rnaComplexProps}
          setNucleotideKeysToRerender={this.setNucleotideKeysToRerender}
        />;
        break;
      }
      default :
        throw new Error("Unhandled switch case");
    }
    return <>
      <b>{tab} custom domains:</b>
      <br/>
      {menu}
    </>;
  }

  public override getHelices() {
    return this.helices;
  }
}
